import { FC, useState, useCallback } from 'react';
import { Container } from '../../layout/Container';
import { FAQItem } from './FAQItem';
import { cn } from '../../../utils/cn';

/**
 * FAQ data structure
 */
interface FAQData {
  question: string;
  answer: string;
}

/**
 * Default FAQ data for the massage therapy services
 */
const defaultFaqs: FAQData[] = [
  {
    question: 'How long is a typical massage session?',
    answer:
      'Our sessions range from 30 minutes to 2 hours. The most popular options are 60 and 90-minute sessions, which allow enough time for a full-body massage and focus on problem areas.',
  },
  {
    question: 'What should I expect during my first visit?',
    answer:
      "During your first visit, you'll complete a brief health intake form and discuss your goals with your therapist. They'll customize the session to your needs and ensure your comfort throughout.",
  },
  {
    question: 'Do I need to undress completely for a massage?',
    answer:
      "You undress to your comfort level. Our therapists are trained in proper draping techniques, and you'll always be covered except for the area being worked on.",
  },
  {
    question: 'How often should I get a massage?',
    answer:
      'It depends on your goals. For general wellness, once a month is great. For chronic pain or athletic recovery, weekly or bi-weekly sessions may be more beneficial.',
  },
  {
    question: "What's your cancellation policy?",
    answer:
      'We require 24 hours notice for cancellations. Late cancellations or no-shows may be subject to a fee.',
  },
  {
    question: 'Do you accept insurance?',
    answer:
      'Some insurance plans cover massage therapy. We provide receipts that you can submit to your insurance for reimbursement. Contact your provider to verify coverage.',
  },
];

export interface FAQSectionProps {
  /**
   * Optional section title
   * @default "Frequently Asked Questions"
   */
  title?: string;
  /**
   * Optional section subtitle
   */
  subtitle?: string;
  /**
   * Optional custom FAQ data (overrides defaults)
   */
  faqs?: FAQData[];
  /**
   * Allow multiple items to be open at once
   * @default false
   */
  allowMultiple?: boolean;
  /**
   * Optional additional className for the section
   */
  className?: string;
}

/**
 * FAQSection component displaying frequently asked questions in accordion format.
 * By default, only one FAQ can be open at a time. Use allowMultiple prop to change this.
 *
 * @example
 * <FAQSection />
 *
 * @example
 * <FAQSection
 *   title="Questions About Our Services"
 *   allowMultiple={true}
 * />
 */
export const FAQSection: FC<FAQSectionProps> = ({
  title = 'Frequently Asked Questions',
  subtitle = 'Find answers to common questions about our massage therapy services',
  faqs = defaultFaqs,
  allowMultiple = false,
  className,
}) => {
  // Track which FAQ item(s) are open
  // When allowMultiple is false, only one index is stored
  // When allowMultiple is true, multiple indices can be stored
  const [openIndices, setOpenIndices] = useState<Set<number>>(new Set());

  const handleToggle = useCallback(
    (index: number) => {
      setOpenIndices((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(index)) {
          // Close this item
          newSet.delete(index);
        } else {
          if (!allowMultiple) {
            // Clear all and open only this one
            newSet.clear();
          }
          newSet.add(index);
        }
        return newSet;
      });
    },
    [allowMultiple]
  );

  return (
    <section
      className={cn('bg-white dark:bg-slate-900 py-16 md:py-20 lg:py-24', className)}
      aria-labelledby="faq-section-title"
    >
      <Container size="lg">
        {/* Section header */}
        <div className="mx-auto mb-12 max-w-2xl text-center md:mb-16">
          <h2
            id="faq-section-title"
            className="mb-4 font-serif text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-100 md:text-4xl"
          >
            {title}
          </h2>
          {subtitle && <p className="text-lg text-gray-600 dark:text-slate-400">{subtitle}</p>}
        </div>

        {/* FAQ accordion */}
        <div className="mx-auto max-w-3xl">
          <div
            className={cn(
              'divide-y divide-gray-200 dark:divide-slate-700',
              'rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800',
              'px-6 shadow-sm dark:shadow-slate-900/30'
            )}
          >
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndices.has(index)}
                onToggle={() => handleToggle(index)}
              />
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

FAQSection.displayName = 'FAQSection';
